import { formatInTimeZone } from 'date-fns-tz';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';
import { useMemo, useState } from 'react';
import Animated, { LinearTransition } from 'react-native-reanimated';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    CheckIcon,
    CircleMinusIcon,
    hitSlop,
    Icon,
    Separator,
    UploadIcon,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import {
    formatDollars,
    formatMaskedValue,
} from '../../../common/utils/bankingUtils';
import { transformBillFromServer } from '../../../common/utils/billUtils';
import type { AccountSummary } from '../../../holistic/types/HolisticSummary';
import useRemoveTransaction from '../../hooks/useRemoveTransaction';
import {
    activeBillsAtom,
    allBillsAtom,
    isEditingBillTransactionsAtom,
    selectedBillAtom,
    selectedReviewBillAtom,
} from '../../state/BillsAtoms';
import {
    type _Bill,
    BillContext,
    type BillTransaction,
    BillTransactionChangeMethod,
    BillTransactionStatus,
    type PartialBill,
} from '../../types/Bill';
import BillDetailsRemoveSheet from './BillDetailsRemoveSheet';

interface BillDetailsTransactionProps {
    transaction: BillTransaction;
    context?: BillContext;
    onPressRemove: (transactionId: string) => void;
}

const BillDetailsTransaction = ({
    transaction,
    context,
    onPressRemove,
}: BillDetailsTransactionProps) => {
    const selectedBill =
        context === BillContext.REVIEW
            ? useAtomValue(selectedReviewBillAtom)
            : useAtomValue(selectedBillAtom);
    const payee = selectedBill?.payee ?? '';

    const isEditingBillTransactions = useAtomValue(
        isEditingBillTransactionsAtom,
    );

    const {
        transactionAmount,
        transactionDate,
        transactionId,
        status,
        institutionName,
        accountMask,
    } = transaction;

    const iconColor =
        status === BillTransactionStatus.REVIEW ? '$orange1000' : '$gray1000';
    const iconBackgroundColor =
        status === BillTransactionStatus.REVIEW ? '$orange400' : '$gray100';

    return (
        <XStack
            paddingVertical={16}
            alignItems="center"
            justifyContent="space-between"
            width="100%"
        >
            <XStack gap={12}>
                <View
                    padding={12}
                    backgroundColor={iconBackgroundColor}
                    borderRadius={12}
                >
                    <Icon icon={UploadIcon} size={20} color={iconColor} />
                </View>
                <YStack gap={4}>
                    <BodyMediumEmphasized color="$black">
                        {payee}
                    </BodyMediumEmphasized>
                    <BodySmallRegular color="$gray1000">
                        {institutionName != null
                            ? `${institutionName} ${formatMaskedValue(accountMask)}`
                            : `Unknown account`}
                    </BodySmallRegular>
                </YStack>
            </XStack>

            <Animated.View layout={LinearTransition.duration(300)}>
                <XStack gap={12}>
                    <View>
                        <YStack gap={4}>
                            <BodyMediumEmphasized
                                textAlign="right"
                                color="$black"
                            >
                                {formatDollars(transactionAmount)}
                            </BodyMediumEmphasized>
                            <BodySmallRegular
                                textAlign="right"
                                color="$gray1000"
                            >
                                {formatInTimeZone(
                                    new Date(transactionDate),
                                    'UTC',
                                    'MMM d, yyyy',
                                )}
                            </BodySmallRegular>
                        </YStack>
                    </View>

                    {isEditingBillTransactions && (
                        <View
                            width={20}
                            height={20}
                            marginTop={1}
                            onPress={() => {
                                onPressRemove(transactionId);
                            }}
                            {...hitSlop(8)}
                        >
                            <Animated.View
                                exiting={LinearTransition.delay(300).duration(
                                    0,
                                )}
                            >
                                <Icon
                                    icon={CircleMinusIcon}
                                    size={20}
                                    color="$red1000"
                                />
                            </Animated.View>
                        </View>
                    )}
                </XStack>
            </Animated.View>
        </XStack>
    );
};

interface BillDetailsTransactionsProps {
    transactions: BillTransaction[];
    account: AccountSummary;
    context?: BillContext;
}

const BillDetailsTransactions = ({
    transactions,
    context,
}: BillDetailsTransactionsProps) => {
    const user = useAuthedUser();
    const toast = useToastController();

    const [isRemoveSheetOpen, setIsRemoveSheetOpen] = useState(false);
    const [selectedTransactionId, setSelectedTransactionId] = useState<
        string | null
    >(null);

    const [isEditingBillTransactions, setIsEditingBillTransactions] = useAtom(
        isEditingBillTransactionsAtom,
    );
    const [selectedBill, setSelectedBill] = useAtom(selectedBillAtom);
    const storeActiveBills = useSetAtom(activeBillsAtom);
    const storeAllBills = useSetAtom(allBillsAtom);

    const { mutate: removeTransaction } = useRemoveTransaction();

    const openRemoveSheet = (transactionId: string) => {
        setIsRemoveSheetOpen(true);
        setSelectedTransactionId(transactionId);
    };

    const closeRemoveSheet = () => {
        setIsRemoveSheetOpen(false);
        setSelectedTransactionId(null);
    };

    const removeTransactionFromBill = () => {
        const updatedBill: _Bill | PartialBill = {
            ...selectedBill,
            transactions: (selectedBill?.transactions ?? []).map(
                (transaction) => {
                    return transaction.transactionId === selectedTransactionId
                        ? {
                              ...transaction,
                              status: BillTransactionStatus.HIDDEN,
                              hiddenBy:
                                  BillTransactionChangeMethod.MANUAL_MEMBER,
                          }
                        : transaction;
                },
            ),
        };

        // optimistically update UI with new bill, and fallback if the update fails
        const oldBill = selectedBill;
        setSelectedBill(updatedBill);

        removeTransaction(
            {
                urlParams: {
                    billId: selectedBill?.id ?? '',
                    membershipId: user?.membership?.id ?? '',
                    transactionId: selectedTransactionId ?? '',
                },
            },
            {
                onSuccess: (updatedBillFromServer) => {
                    const transformedBill = transformBillFromServer(
                        updatedBillFromServer,
                    );

                    storeActiveBills((prev) => {
                        return prev.map((pb) => {
                            return pb.id === transformedBill.id
                                ? transformedBill
                                : pb;
                        });
                    });
                    storeAllBills((prev) => {
                        return prev.map((pb) => {
                            return pb.id === transformedBill.id
                                ? transformedBill
                                : pb;
                        });
                    });
                    toast.show('Excluded', {
                        description: 'Bill updated',
                        icon: (
                            <View
                                alignItems="center"
                                justifyContent="center"
                                backgroundColor="$green1000"
                                borderRadius={12}
                                padding={9}
                            >
                                <Icon
                                    icon={CheckIcon}
                                    size={24}
                                    color="$white"
                                />
                            </View>
                        ),
                    });
                    setSelectedBill(transformedBill);
                },
                onError: () => {
                    setSelectedBill(oldBill);
                },
                onSettled: () => {
                    setIsRemoveSheetOpen(false);
                },
            },
        );
    };

    const sortedTransactions = useMemo(
        () =>
            (transactions ?? [])
                .filter((transaction) =>
                    [
                        BillTransactionStatus.ACTIVE,
                        BillTransactionStatus.REVIEW,
                    ].includes(transaction.status),
                )
                // sort by transaction date, desc
                .sort((trxA, trxB) =>
                    trxA.transactionDate < trxB.transactionDate ? 1 : -1,
                ),
        [transactions],
    );

    return (
        <YStack marginTop={16} separator={<Separator />}>
            <XStack paddingVertical={12} justifyContent="space-between">
                <BodyMediumRegular color="$gray1000">
                    Related transactions
                </BodyMediumRegular>
                {context !== BillContext.REVIEW &&
                    sortedTransactions.length > 0 && (
                        <BodyMediumEmphasized
                            onPress={() => {
                                setIsEditingBillTransactions(
                                    !isEditingBillTransactions,
                                );
                            }}
                        >
                            {isEditingBillTransactions ? 'Done' : 'Edit'}
                        </BodyMediumEmphasized>
                    )}
            </XStack>

            <YStack separator={<Separator />}>
                {sortedTransactions.length > 0 ? (
                    sortedTransactions.map((transaction) => {
                        const { transactionId } = transaction;

                        return (
                            <BillDetailsTransaction
                                key={transactionId}
                                transaction={transaction}
                                context={context}
                                onPressRemove={() => {
                                    openRemoveSheet(transactionId);
                                }}
                            />
                        );
                    })
                ) : (
                    <BodyMediumEmphasized textAlign="center" paddingTop={20}>
                        Apologies, we are unable to fetch transactions at this
                        time. Please check back later.
                    </BodyMediumEmphasized>
                )}
            </YStack>

            {isRemoveSheetOpen && (
                <BillDetailsRemoveSheet
                    header="Exclude transaction"
                    title="Are you sure you want to exclude this transaction?"
                    subText="Excluding a transaction will update the details for this bill."
                    editMode={true}
                    onExclude={removeTransactionFromBill}
                    onDismiss={closeRemoveSheet}
                />
            )}
        </YStack>
    );
};

export default BillDetailsTransactions;
